import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

/**
 * Resilient auth storage.
 *
 * Supabase persists the session in localStorage by default. On Safari/WebKit
 * with strict privacy settings (ITP), Private Browsing, or storage partitioning,
 * any localStorage access can THROW (SecurityError / QuotaExceededError). With the
 * default client that throw bubbles up and silently breaks session persistence —
 * the user authenticates but the session never sticks, so the flow hangs forever
 * on "Connecting…". This is the classic "works on my Mac, not my friend's Mac" bug.
 *
 * We wrap localStorage in try/catch and transparently fall back to an in-memory
 * store. The normal case is byte-for-byte identical to the default; the broken
 * case degrades gracefully instead of throwing.
 */
function createResilientStorage() {
  const memory = new Map();

  const canUseLocalStorage = (() => {
    try {
      const k = '__iseo_ls_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch (_) {
      return false;
    }
  })();

  if (canUseLocalStorage) {
    return {
      getItem: (key) => {
        try { return window.localStorage.getItem(key); }
        catch (_) { return memory.has(key) ? memory.get(key) : null; }
      },
      setItem: (key, value) => {
        try { window.localStorage.setItem(key, value); }
        catch (_) { memory.set(key, value); }
      },
      removeItem: (key) => {
        try { window.localStorage.removeItem(key); }
        catch (_) { memory.delete(key); }
      },
    };
  }

  // localStorage is entirely unavailable — pure in-memory (survives the page session).
  return {
    getItem: (key) => (memory.has(key) ? memory.get(key) : null),
    setItem: (key, value) => { memory.set(key, value); },
    removeItem: (key) => { memory.delete(key); },
  };
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Explicit (matches Supabase defaults) so intent is documented and resilient.
    persistSession: true,
    autoRefreshToken: true,   // keep the access token fresh in long-lived tabs
    detectSessionInUrl: true, // parse the OAuth/magic-link redirect on load
    // NOTE: intentionally no custom storageKey — keep Supabase's default key so
    // existing logged-in sessions survive this deploy (a custom key would log
    // everyone out once).
    storage: createResilientStorage(),
  },
  global: {
    headers: { 'x-client-info': 'improveseo-cms' },
  },
})

// Types for JavaScript (using JSDoc comments)

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} email
 * @property {string} [name]
 * @property {string} api_key
 * @property {number} [plan_id]
 * @property {string} [auth_user_id]
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} Website
 * @property {number} id
 * @property {number} user_id
 * @property {string} domain
 * @property {string} site_code
 * @property {boolean} active
 * @property {string} created_at
 */

/**
 * @typedef {Object} CreditBalance
 * @property {number} id
 * @property {number} user_id
 * @property {'iseo'|'images'|'content'|'keywords'} credit_type
 * @property {number} amount
 * @property {string} updated_at
 */

/**
 * @typedef {Object} SubscriptionPlan
 * @property {number} id
 * @property {string} slug
 * @property {string} name
 * @property {number} image_credits
 * @property {number} content_credits
 * @property {number} keyword_credits
 * @property {number|null} website_limit
 * @property {boolean} scheduled_publishing
 * @property {boolean} course_access
 * @property {number} price_cents
 */

/**
 * @typedef {Object} Generation
 * @property {number} id
 * @property {number} user_id
 * @property {number} website_id
 * @property {'active'|'testing'} type
 * @property {'success'|'error'|'pending'} status
 * @property {any} result_meta
 * @property {string} content
 * @property {number} processing_time_ms
 * @property {string} session_id
 * @property {string} created_at
 */